import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  HandMetal,
  Loader2,
  RefreshCcw,
  ShieldCheck,
  ShieldOff,
  Copy as CopyIcon,
  ArrowLeftRight,
  Package,
  Check,
  X as XIcon,
} from 'lucide-react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';

/**
 * PassModal — sender-side of the Pass flow.
 *
 * Handles three modes:
 *   copy   → generates a QR that duplicates a single track to the scanner
 *   trade  → generates a QR that starts a mutual-swap flow
 *   crate  → generates a QR that offers the whole crate to the scanner
 *
 * For Trade: after the QR is generated, we poll /pass/{token}/status until
 * a counter-offer arrives, then let the user finalize the trade.
 */
export default function PassModal({
  open,
  onClose,
  track = null,          // required for copy/trade
  bucket = null,         // required for crate
  defaultMode = 'copy',  // 'copy' | 'trade' | 'crate'
}) {
  const [mode, setMode] = useState(defaultMode);
  const [signed, setSigned] = useState(true);
  const [token, setToken] = useState(null);
  const [expiresIn, setExpiresIn] = useState(0);
  const [creating, setCreating] = useState(false);
  const [tradeStatus, setTradeStatus] = useState(null); // { state, counter_offer? }
  const [finalizing, setFinalizing] = useState(false);
  const timerRef = useRef(null);
  const pollRef = useRef(null);

  // Reset local state whenever the modal is reopened.
  useEffect(() => {
    if (open) {
      setMode(defaultMode);
      setSigned(true);
      setToken(null);
      setExpiresIn(0);
      setTradeStatus(null);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [open, defaultMode]);

  // Available modes depend on what the caller passed us — you can't
  // pass a whole crate if we only have a single track handle, etc.
  const availableModes = useMemo(() => {
    const modes = [];
    if (track) modes.push('copy', 'trade');
    if (bucket) modes.push('crate');
    return modes;
  }, [track, bucket]);

  const handleGenerate = useCallback(async () => {
    setCreating(true);
    try {
      const body = { mode, signed };
      if (mode === 'copy' || mode === 'trade') body.card_id = track?.id;
      if (mode === 'crate') body.bucket = bucket;
      const { data } = await api.post('/pass/create', body);
      setToken(data.token);
      setExpiresIn(data.expires_in_seconds || 90);
      setTradeStatus(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate Pass');
    } finally {
      setCreating(false);
    }
  }, [mode, signed, track, bucket]);

  // Countdown timer + expiry handling.
  useEffect(() => {
    if (!token || expiresIn <= 0) return;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setExpiresIn((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [token, expiresIn > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll for trade counter-offers.
  useEffect(() => {
    if (!token || mode !== 'trade') return;
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/pass/${token}/status`);
        setTradeStatus(data);
        if (data?.state === 'accepted') {
          clearInterval(pollRef.current);
          toast.success('Trade completed');
          onClose?.();
        }
      } catch (_) { /* ignore */ }
    }, 2500);
    return () => clearInterval(pollRef.current);
  }, [token, mode, onClose]);

  const handleFinalizeTrade = useCallback(async () => {
    if (!token || !tradeStatus?.counter_offer) return;
    setFinalizing(true);
    try {
      await api.post(`/pass/${token}/accept`, {
        target_bucket: tradeStatus.counter_offer.target_bucket || null,
      });
      toast.success('Trade complete — track added to your crate');
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to finalize trade');
    } finally {
      setFinalizing(false);
    }
  }, [token, tradeStatus, onClose]);

  // QR payload: encode a full URL so the app can deep-link back into the
  // scan flow. Fallback to just the token if we ever ship a native app.
  const qrValue = token
    ? `${typeof window !== 'undefined' ? window.location.origin : ''}/pass/${token}`
    : '';

  const expired = token && expiresIn <= 0 && tradeStatus?.state !== 'trade_pending';

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? null : onClose?.())}>
      <DialogContent
        className="max-w-md p-0 gap-0 overflow-hidden"
        data-testid="pass-modal"
      >
        <DialogHeader className="px-4 pt-4 pb-3 border-b border-border">
          <DialogTitle className="flex items-center gap-2 text-base">
            <HandMetal className="w-4 h-4 text-[hsl(var(--primary))]" />
            Pass to a GroovLenser
          </DialogTitle>
          <DialogDescription className="text-xs leading-relaxed">
            Hold your phones close. The other GroovLenser scans this QR — the
            track lands in their collection instantly.
          </DialogDescription>
        </DialogHeader>

        <div className="p-4 space-y-4">
          {/* Mode selector — hidden after QR generated to reduce visual load */}
          {!token && (
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">
                Mode
              </Label>
              <div className="mt-2 grid grid-cols-3 gap-1.5">
                <ModeButton
                  active={mode === 'copy'}
                  disabled={!availableModes.includes('copy')}
                  onClick={() => setMode('copy')}
                  icon={<CopyIcon className="w-3.5 h-3.5" />}
                  label="Copy"
                  hint="Send a copy"
                  testid="pass-mode-copy"
                />
                <ModeButton
                  active={mode === 'trade'}
                  disabled={!availableModes.includes('trade')}
                  onClick={() => setMode('trade')}
                  icon={<ArrowLeftRight className="w-3.5 h-3.5" />}
                  label="Trade"
                  hint="Swap tracks"
                  testid="pass-mode-trade"
                />
                <ModeButton
                  active={mode === 'crate'}
                  disabled={!availableModes.includes('crate')}
                  onClick={() => setMode('crate')}
                  icon={<Package className="w-3.5 h-3.5" />}
                  label="Crate"
                  hint="Share crate"
                  testid="pass-mode-crate"
                />
              </div>
            </div>
          )}

          {/* Signed toggle */}
          {!token && (
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div className="flex items-start gap-2 min-w-0">
                {signed ? (
                  <ShieldCheck className="w-4 h-4 text-[hsl(var(--primary))] mt-0.5" />
                ) : (
                  <ShieldOff className="w-4 h-4 text-muted-foreground mt-0.5" />
                )}
                <div className="min-w-0">
                  <div className="text-sm font-medium">Sign this Pass</div>
                  <div className="text-[11px] text-muted-foreground leading-relaxed">
                    Attach your username so the recipient sees who passed it.
                  </div>
                </div>
              </div>
              <Switch checked={signed} onCheckedChange={setSigned} data-testid="pass-signed-switch" />
            </div>
          )}

          {/* Track / Crate preview */}
          {(track || bucket) && !token && (
            <div className="rounded-lg border border-border p-2.5 flex items-center gap-2.5">
              {track?.album_art_url && (
                <img
                  src={track.album_art_url}
                  alt=""
                  className="w-11 h-11 rounded object-cover shrink-0"
                />
              )}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">
                  {mode === 'crate' ? `${bucket} crate` : (track?.title || 'Untitled')}
                </div>
                <div className="text-[11px] text-muted-foreground truncate">
                  {mode === 'crate' ? 'Whole crate — recipient picks tracks' : (track?.artist || '')}
                </div>
              </div>
            </div>
          )}

          {/* Generate button */}
          {!token && (
            <Button
              onClick={handleGenerate}
              disabled={creating || !availableModes.includes(mode)}
              className="w-full rounded-lg h-10"
              data-testid="pass-generate-button"
            >
              {creating ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <HandMetal className="w-4 h-4 mr-1.5" />
              )}
              Generate QR
            </Button>
          )}

          {/* QR + countdown */}
          {token && !tradeStatus?.counter_offer && (
            <div className="flex flex-col items-center gap-3">
              <div className="crate-neon-border rounded-2xl" style={{ padding: '4px' }}>
                <div className="bg-white p-4 rounded-[14px]">
                  <QRCodeSVG
                    value={qrValue}
                    size={220}
                    level="H"
                    imageSettings={
                      track?.album_art_url && mode !== 'crate'
                        ? {
                            src: track.album_art_url,
                            height: 60,
                            width: 60,
                            excavate: true,
                          }
                        : undefined
                    }
                  />
                </div>
              </div>
              <div className="text-center">
                {expired ? (
                  <div className="text-sm text-[hsl(var(--destructive))] font-medium">
                    Pass expired — generate a new one
                  </div>
                ) : (
                  <>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                      Expires in
                    </div>
                    <div className="text-2xl font-display font-semibold tabular-nums">
                      {expiresIn}s
                    </div>
                    {mode === 'trade' && (
                      <div className="text-[11px] text-muted-foreground mt-1">
                        Waiting for the other GroovLenser to offer a track back…
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="flex gap-2 w-full">
                <Button
                  onClick={handleGenerate}
                  disabled={creating}
                  variant="outline"
                  size="sm"
                  className="rounded-lg flex-1"
                  data-testid="pass-regenerate-button"
                >
                  <RefreshCcw className="w-3.5 h-3.5 mr-1.5" />
                  New QR
                </Button>
                <Button
                  onClick={() => onClose?.()}
                  variant="ghost"
                  size="sm"
                  className="rounded-lg flex-1"
                  data-testid="pass-close-button"
                >
                  <XIcon className="w-3.5 h-3.5 mr-1.5" />
                  Done
                </Button>
              </div>
            </div>
          )}

          {/* Trade counter-offer confirmation */}
          {token && tradeStatus?.counter_offer && (
            <div className="space-y-3">
              <div className="text-sm font-medium text-foreground">
                Counter-offer received
              </div>
              <div className="rounded-lg border border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/5 p-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">
                  @{tradeStatus.counter_offer.username} offers you:
                </div>
                <div className="flex items-center gap-2.5">
                  {tradeStatus.counter_offer.snapshot?.album_art_url && (
                    <img
                      src={tradeStatus.counter_offer.snapshot.album_art_url}
                      alt=""
                      className="w-11 h-11 rounded object-cover shrink-0"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">
                      {tradeStatus.counter_offer.snapshot?.title || 'Untitled'}
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {tradeStatus.counter_offer.snapshot?.artist || ''}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleFinalizeTrade}
                  disabled={finalizing}
                  className="rounded-lg flex-1"
                  data-testid="pass-finalize-trade-button"
                >
                  {finalizing ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <Check className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  Accept trade
                </Button>
                <Button
                  variant="outline"
                  onClick={() => onClose?.()}
                  disabled={finalizing}
                  className="rounded-lg flex-1"
                  data-testid="pass-cancel-trade-button"
                >
                  <XIcon className="w-3.5 h-3.5 mr-1.5" />
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ModeButton({ active, disabled, onClick, icon, label, hint, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`rounded-lg border p-2 text-left transition-colors ${
        active
          ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]'
          : disabled
          ? 'border-border/40 opacity-50 cursor-not-allowed'
          : 'border-border hover:border-foreground/40'
      }`}
    >
      <div className="flex items-center gap-1.5">
        {icon}
        <span className="text-xs font-medium">{label}</span>
      </div>
      <div className="text-[9px] text-muted-foreground mt-0.5 font-mono uppercase tracking-widest">
        {hint}
      </div>
    </button>
  );
}
